import React from 'react';

const PackagesForDevelopers = () => {
  return (
    <div className="container mx-auto px-4 py-12">
      <header className="max-w-3xl mx-auto text-center mb-8">
        <h1 className="text-4xl font-extrabold leading-tight">Packages for developers</h1>
        <h2 className="text-4xl leading-tight">Tracing Post‑Quantum Cryptography</h2>
        <p className="mt-4 text-gray-600">A concise catalogue of developer libraries and tools for detecting, tracing and inspecting indicators of post‑quantum cryptography (PQC) in web traffic and software environments.</p>
      </header>

      <main className="max-w-3xl mx-auto space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-2">Why PQC awareness matters</h2>
          <p className="text-gray-700">The transition to post‑quantum cryptography is an emerging reality. For developers and organisations, visibility into which cryptographic mechanisms are actually in use is essential for auditing, compliance, risk assessment, migration planning and security validation. These packages provide practical ways to surface whether web requests or components are negotiating PQC or hybrid key exchanges so teams can make informed decisions.</p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-2">How these packages help</h2>
          <p className="text-gray-700">The libraries and tools listed below are intended to help developers and security teams gain technical insight into PQC usage. Depending on the ecosystem, they can be used to analyse TLS handshakes, inspect connection metadata, or integrate tracing into application and testing workflows. Use them to collect evidence of PQC negotiation, to feed audit logs, or to validate test environments during a migration to PQC-capable stacks.</p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">Available packages</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-medium">.NET / NuGet</h3>
              <p className="text-gray-700">ConnectingApps.PqcTracer — a NuGet package that integrates with .NET applications and tooling. See the package page at <a href="https://www.nuget.org/packages/ConnectingApps.PqcTracer" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">NuGet: ConnectingApps.PqcTracer</a>.</p>
            </div>

            <div>
              <h3 className="text-xl font-medium">npm (Node.js)</h3>
              <p className="text-gray-700">pqc-tracer — a Node.js/npm package for developers building JavaScript/TypeScript tooling and test clients. Documentation and releases are available at <a href="https://www.npmjs.com/package/pqc-tracer" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">npm: pqc-tracer</a>.</p>
            </div>

            <div>
              <h3 className="text-xl font-medium">Python / PyPI</h3>
              <p className="text-gray-700">pqcreader — a Python package published to PyPI suitable for scripting, automation and integration into analysis pipelines. Find it on PyPI: <a href="https://pypi.org/project/pqcreader" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">pqcreader</a>.</p>
            </div>

            <div>
              <h3 className="text-xl font-medium">Rust / crates.io</h3>
              <p className="text-gray-700">pqctracer — a Rust crate for systems-level tooling and high-performance analysis. See releases and documentation at <a href="https://crates.io/crates/pqctracer" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">crates.io: pqctracer</a>.</p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-2">About the projects</h2>
          <p className="text-gray-700">These packages were developed by Connecting Apps and QuantumSafeAudit.com. The code and maintenance work were performed by Daan Acohen, founder of both organisations. The projects aim to provide reliable, auditable signals about PQC usage rather than to replace full protocol implementations or cryptographic libraries.</p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-2">Get started</h2>
          <p className="text-gray-700">Choose the package that best matches your stack and follow the linked package page for installation and usage instructions. If you need help evaluating PQC readiness or integrating these tools into CI/CD and audit processes, contact the teams at Connecting Apps or QuantumSafeAudit for professional services.</p>
        </section>

        <footer className="pt-6 border-t">
          <p className="text-gray-600">©{new Date().getFullYear()} Connecting Apps · QuantumSafeAudit.com — coding by Daan Acohen. Links open in a new tab.</p>
        </footer>
      </main>
    </div>
  );
};

export default PackagesForDevelopers;

